/**
 * FreeCreate 动作 hook —— 草稿恢复 / 保存草稿 / AI 润色 / 生成海报。
 *
 * 把所有 mutation + navigate + toast 副作用从 page 里抽出来,page 只负责拼 UI。
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import { toast } from "@/shared/hooks/use-toast";
import { acquireQuoteId } from "@/entities/pricing/model/useQuote";
import { useBrandAssets } from "@/entities/brand-asset";
import { sceneLabelOf, sceneUsageOf } from "@/entities/scene-card/catalog";
import {
  posterService,
  type NormalizeBrandAssetsMeta,
  type PosterTaskCreateRequest,
  type Provider,
  type ResolutionTier,
} from "../../api/posterService";
import { posterErrorToast } from "@/shared/lib/poster-errors";
import {
  FONT_STYLES,
  FONT_STYLE_AUTO,
  fontPhraseForKey,
} from "../../ui/components/free-create/fontStyleOptions";
import { parseStyleTokens } from "../style/parseStyleTokens";
import { buildCreativeOverrides } from "./buildCreativeOverrides";
import { buildLayoutOverrides } from "./buildLayoutOverrides";
import {
  RAW_PROMPT_MAX,
  absorbedMaterials,
  buildRawPrompt,
  capPolishResult,
  rawPromptOverflow,
} from "./buildRawPrompt";
import { parseFreeCreateProduct } from "./productAdapter";
import {
  parseDraftRefImages,
  refImagesBlockedReason,
  toDraftRefImages,
  toRefImageRefs,
} from "./multiRefImages";
import { ratioCompositionPhrase } from "./ratioComposition";
import {
  submittableGroupPoints,
  shouldInferRatio,
  detectOutputMode,
  untouchedReferenceTitles,
} from "./outputMode";
import { buildStyleName } from "./buildStyleName";
import { submitQuoteParams } from "./submitQuote";
import { flatTaskName, posterTaskName, promptHead } from "../taskName";
import type { Count, Ratio, ScheduleMode } from "./useFreeCreateForm";
import type { FreeCreateForm } from "./useFreeCreateForm";
import {
  detectRatioDetailedFromInputs,
  detectRatioFromInputs,
  resolveRatioRuling,
} from "./detectRatio";
import { errorText } from "@/shared/lib/apiErrors";
import { sliceSafe } from "@/shared/lib/sliceSafe";

type BrandData = NonNullable<ReturnType<typeof useBrandAssets>["data"]>;

interface ActionsArgs {
  form: FreeCreateForm;
  brand: BrandData | undefined;
  validRatio: Ratio;
  validTier: ResolutionTier | null;
  validCount: Count;
  /**
   * 出图前红线闸(2026-08-14):蓝图带来的参考句一字未改就点生成时问一次,返回 false 则中止。
   *
   * ★为什么用回调注入而不在这里 `useConfirm()`:那样 hook 就硬依赖 `<ConfirmProvider>`,
   *   仓内 8 个既有测试文件的 renderHook wrapper 都没有它 —— 实测当场红了 52 条。
   *   弹窗归 UI 层,hook 只负责「问不问、答不答应」这条流程,两边都好测。
   * ★缺省(测试/存量调用方不传)= 不拦,行为与改动前逐字相同。
   */
  confirmUntouchedReference?: (titles: string[]) => Promise<boolean>;
  /**
   * W2-B9:当前引擎支不支持多图参考(能力表 `supportsMultiRef`)。只在带了参考图时才读;
   * 缺省 = 不拦(存量调用方 / 测试不传时行为不变,真拦截还有 ai 侧 422 兜底)。
   */
  multiRefSupported?: boolean;
}

export interface FreeCreateActions {
  saveDraftPending: boolean;
  saveDraft: () => void;
  /** Wave 3 — AI 优化按钮:Qwen 归整 full mode → 两段式产物。替代旧 handlePolish。 */
  handleAiOptimize: () => Promise<void>;
  /** Wave 3 — 恢复原文:textarea 回到 AI 优化前快照,清两段式 state。 */
  handleRestoreOriginal: () => void;
  /** Wave 3 — 重新优化:用原文(不是当前 textarea 改过的)重跑 Qwen 归整。 */
  handleReOptimize: () => Promise<void>;
  /** Wave 4 (2026-05-18) — 风格 chip 切换入口:已 AI 优化时自动 re-run normalize 带新 userStyle。 */
  handleStyleChange: (newStyle: string | null) => void;
  /**
   * 出图。`resolveMask` 只在改图态且用户涂过时由页面传入:**提交这一刻**才导出 mask 并上传,
   * 返回 objectKey(没涂 → null)。放在这里而不是每笔预传,是因为预传那套结构天生带竞态窗口
   * (codex 连抓 5 轮:上传竞态 / 清空卡死 / 导出层竞态 / 两笔共用令牌 / 抬笔到回调的窗口)。
   * 抛错 = 中止提交并告诉用户,不静默退化成整图重绘。
   */
  handleGenerate: (
    resolveMask?: () => Promise<File | null>,
    /** B2-1 分步:"cover" 只出封面(扣 1 张的钱),剩下的走详情页续出。 */
    groupStage?: "cover" | "all",
    /**
     * 批 L:确认卡上那张**正式报价单**的单号。
     *
     * ★ 必须一路带到建单请求里:后端按 `(tenant, quoteId)` 把单绑到 action_id,
     *   结算时按单兑现。**没有单的任务会静默回退到旧的成本 ×10 口径** ——
     *   用户被按 6.25 倍扣,而两侧日志都干干净净。
     */
    quoteId?: string | null,
    /**
     * 确认卡上用户过目/改过的画幅(2026-09-09,语义见下方实现处注释)。
     * ★0910 补:实现加了这一参、接口声明漏了 —— 调用方(FreeCreateBody/确认卡测试)
     *   一直在传第四参,tsc 报 TS2554 挂了两天;运行时 JS 忽略签名所以行为一直是对的,
     *   这里只是把声明追平实现。
     */
    confirmedRatio?: Ratio | null
  ) => Promise<void>;
}

export function useFreeCreateActions({
  form,
  brand,
  validRatio,
  validTier,
  validCount,
  confirmUntouchedReference,
  multiRefSupported = true,
}: ActionsArgs): FreeCreateActions {
  const navigate = useNavigate();

  // V26 (2026-05-14) Wave 4.4 — 草稿入口:URL ?draftId= 恢复
  const draftRestoredRef = useRef(false);
  /**
   * ★0811 报障「提示词被优化没了」(娜娜)· 归整回写守卫:
   * - normalizeSeqRef:请求序号 —— 后台重跑不 await、无取消,慢返程会盖快返程;
   *   只有最新一发的返程许写。
   * - lastNormalizedRef:上次归整写进 contentDescription 的逐字内容 ——
   *   用户在框里手改过(现值 ≠ 上次写入)后,后台重跑(换产品/画风触发)**不许**
   *   整段覆盖他的字;此前 `setContentDescription(resp ?? "")` 无条件盖,
   *   回包缺字段时还直接清空。
   */
  const normalizeSeqRef = useRef(0);
  const lastNormalizedRef = useRef<string | null>(null);
  useEffect(() => {
    const draftIdParam = form.draftIdParam;
    if (!draftIdParam || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    posterService.getDraft(Number(draftIdParam)).then(
      (d) => {
        try {
          const f = JSON.parse(d.formJson) as Record<string, unknown>;
          if (typeof f.prompt === "string") form.setPrompt(f.prompt);
          if (typeof f.ratio === "string") form.setRatio(f.ratio as Ratio);
          if (typeof f.logoId === "string" || f.logoId === null)
            form.setLogoId(f.logoId as string | null);
          if (typeof f.qrId === "string" || f.qrId === null) form.setQrId(f.qrId as string | null);
          if (typeof f.useBrandColors === "boolean") form.setUseBrandColors(f.useBrandColors);
          if (typeof f.printContact === "boolean") form.setPrintContact(f.printContact);
          if (typeof f.useGptThinking === "boolean") form.setUseGptThinking(f.useGptThinking);
          if (typeof f.count === "number") form.setCount(f.count as Count);
          if (typeof f.provider === "string") form.setProvider(f.provider as Provider);
          if (f.resolutionTier === null || typeof f.resolutionTier === "string")
            form.setResolutionTier(f.resolutionTier as ResolutionTier | null);
          if (typeof f.scheduleMode === "string")
            form.setScheduleMode(f.scheduleMode as ScheduleMode);
          if (typeof f.scheduledAt === "string" || f.scheduledAt === null)
            form.setScheduledAt(f.scheduledAt as string | null);
          // #13 i2i 重传:恢复模式 + 按 objectKey 重签参考图预览(异步,失败不阻断其它字段)。
          if (f.mode === "T2I" || f.mode === "I2I") form.setMode(f.mode);
          if (typeof f.extraConstraint === "string") form.setExtraConstraint(f.extraConstraint);
          //
          // V0 (2026-07-25) — 风格三件套(画风 / 配色 / 字体档)进草稿。
          // ★它们**都改变出图**,却一直没被草稿带走:存了草稿再打开,画风与字体悄悄回默认,
          //   出来的图和存之前不是一张。配色是本波新加的,一并补齐,不留半套。
          // ★★配色必须验型再回填(`parseStyleTokens`):陈年/被写坏的 formJson 里
          //   多一个字段或野一个枚举,上送就是 422(ai `extra="forbid"`)。
          if (typeof f.selectedStyle === "string" || f.selectedStyle === null)
            form.setSelectedStyle(f.selectedStyle as string | null);
          if (typeof f.fontStyle === "string" && FONT_STYLES.some((x) => x.key === f.fontStyle))
            form.setFontStyle(f.fontStyle);
          const tokens = parseStyleTokens(f.styleTokens);
          // 只有"验型通过 + 当时确实是用户自己选的"才回填 —— 跟随态下配色是画风的函数,
          // 上面 setSelectedStyle 已经把它推出来了,再塞一遍反而会把跟随态错误地熄灭。
          if (tokens && f.followStyle === false) form.setStyleTokens(tokens);
          // 波B — 产品快照:形状不对整包丢弃(半份脏数据会一路进出图指令,
          // 而界面上看起来像"产品库里就是这么写的")。
          const restoredProduct = parseFreeCreateProduct(f.product);
          if (restoredProduct) form.setProduct(restoredProduct);
          if (typeof f.showProductSpecs === "boolean") form.setShowProductSpecs(f.showProductSpecs);
          const cover = f.coverImageRef;
          if (typeof cover === "string" && cover) {
            posterService.presignUpload(cover).then(
              (url) => form.setRefImage({ objectKey: cover, tempUrl: url, previewUrl: url }),
              () => {
                /* 重签失败:i2i 参考图需重传,不阻断其它字段恢复 */
              }
            );
          }
          // 1013 P10:多图参考(W2-B9)也跟草稿走 —— 按 objectKey 逐张重签预览,保序;
          //   某张重签失败只丢那一张(需重传),不阻断其它字段/其它张。
          const refs = parseDraftRefImages(f.refImages);
          if (refs.length > 0) {
            void Promise.all(
              refs.map((r) =>
                posterService.presignUpload(r.objectKey).then(
                  (url) => ({ ...r, previewUrl: url }),
                  () => null
                )
              )
            ).then((got) => form.setRefImages(got.filter((x) => x !== null)));
          }
          toast({ title: "已恢复草稿", description: d.name });
        } catch {
          toast({ title: "草稿恢复失败,可能格式异常", variant: "destructive" });
        }
      },
      () => {
        toast({ title: "草稿加载失败", variant: "destructive" });
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.draftIdParam]);

  // ───────── 默认资产同步 ─────────
  // 2026-05-18: 删自动 pickDefault — 默认"本次不使用",让用户主动选品牌资产再写文案,
  // AI 优化时上下文完整(brand assets 状态明确)。
  // 老 V7.2 用 brandInitializedRef + pickDefault 在 react-query 重 fetch brand 时
  // 自动选默认 logo/QR,会让用户没注意就启用了 brand assets。删。

  // ───────── 保存草稿 mutation ─────────
  const saveDraftMut = useMutation({
    mutationFn: () =>
      posterService.upsertDraft({
        draftId: form.currentDraftId,
        name: promptHead(form.prompt) || flatTaskName(form.prompt) || "未命名草稿",
        currentStep: 1,
        formJson: JSON.stringify({
          prompt: form.prompt,
          ratio: form.ratio,
          logoId: form.logoId,
          qrId: form.qrId,
          useBrandColors: form.useBrandColors,
          printContact: form.printContact,
          useGptThinking: form.useGptThinking,
          count: form.count,
          provider: form.provider,
          resolutionTier: form.resolutionTier,
          scheduleMode: form.scheduleMode,
          scheduledAt: form.scheduledAt,
          // #13 i2i 重传:持久化模式 + 参考图 objectKey(恢复时按 key 重签预览,不再离页即丢)。
          mode: form.mode,
          coverImageRef: form.refImage?.objectKey ?? null,
          // 1013 P10:多图参考(objectKey + 用途;预览 URL 是临时签名,不存)
          refImages: toDraftRefImages(form.refImages),
          // R5 涂选区域(codex 七轮):本条**故意不进草稿** —— 存了就得能在画布上还原成红区,
          // 否则恢复出来是一张"看不见的 mask":界面显示没涂、实际按局部重绘走,比丢了更糟。
          // 真还原要把 mask PNG 画回离屏画布(跨域图会污染 canvas → getImageData/toBlob 抛),
          // 需要真 MinIO 验 CORS,本批本地验证做不到。所以改成**存的时候就说清楚**(见下方 toast),
          // 恢复后画布是空的 = 按整张图重绘,所见即所得。
          // #1 特别要求也一并持久化。
          extraConstraint: form.extraConstraint,
          // V0 — 风格三件套(见恢复处注释:改出图的东西必须跟着草稿走)
          selectedStyle: form.selectedStyle,
          fontStyle: form.fontStyle,
          styleTokens: form.styleTokens,
          followStyle: form.followStyle,
          // 波B — 产品库直选的快照。★同上一条理由:它**改变出图**(名称/卖点/参数逐字
          // 进指令),不跟着草稿走 = 存了再打开,出来的图和存之前不是一张。
          product: form.product,
          // 「在图上显示产品参数」同样改变出图,跟着草稿走
          showProductSpecs: form.showProductSpecs,
        }),
      }),
    onSuccess: (d) => {
      form.setCurrentDraftId(d.draftId);
      toast({
        title: "已保存草稿",
        // 涂选区域不进草稿(理由见上面 coverImageRef 旁的注释)——**存的时候就说**,
        // 别让用户下次打开才发现圈选没了(那时候他已经按"圈过"在操作了)。
        description: form.maskPainted
          ? "在视觉舱工作台找到;涂选的区域不会一起保存,下次打开需要重新涂"
          : "在视觉舱工作台找到",
      });
    },
    onError: (e: unknown) => {
      const t = posterErrorToast(e);
      toast(t);
    },
  });

  function buildBrandAssetsMeta(): NormalizeBrandAssetsMeta {
    const logoEntry = form.logoId ? brand?.logos.find((l) => l.id === form.logoId) : undefined;
    const qrEntry = form.qrId ? brand?.qrs.find((q) => q.id === form.qrId) : undefined;
    return {
      logo: logoEntry
        ? {
            exists: true,
            aspect: (logoEntry as { aspect?: "SQUARE" | "WIDE" | "TALL" }).aspect ?? null,
            hasTransparentBg:
              (logoEntry as { hasTransparentBg?: boolean }).hasTransparentBg ?? null,
            colorHex: (logoEntry as { colorHex?: string }).colorHex ?? null,
          }
        : { exists: false },
      qrcode: qrEntry ? { exists: true } : { exists: false },
    };
  }

  async function runNormalize(
    sourceText: string,
    preserveSnapshot: string | null,
    /** 2026-05-18 — 显式 override:chip 变更同步 setState + 调 normalize 时,
     *  React state 更新是异步的,closure 里读 form.selectedStyle 拿的是旧值。
     *  让调用方直接传新值进来,避免 stale read。undefined 表示读 closure state。 */
    userStyleOverride?: string | null,
    /** 0811:显式重跑(用户亲手点优化/重新优化)= 他要的就是替换 —— 手改守卫不拦;
     *  只有后台指纹重跑(换产品/画风触发)才守。 */
    opts: { explicit?: boolean } = {}
  ) {
    if (!sourceText.trim()) return;
    const seq = ++normalizeSeqRef.current; // 见 normalizeSeqRef 注释:只有最新一发许写
    form.setPolishing(true);
    form.setOptimizeFailureMessage(null);
    try {
      const styleForReq = userStyleOverride !== undefined ? userStyleOverride : form.selectedStyle;
      const fontPhrase =
        form.fontStyle && form.fontStyle !== FONT_STYLE_AUTO
          ? fontPhraseForKey(form.fontStyle)
          : null;
      /**
       * 归整层该被告知的画幅 —— **只在真定下来时**才有值(见下方 context.ratio 注释)。
       * 手选过 → 用它;没手选 → 看文案里有没有显式方向/渠道词(与提交侧
       * `detectRatioFromInputs` 同一个函数,不另写一份判据);都没有 → null,不发字段。
       */
      const ratioForNormalize: Ratio | null = form.ratioUserTouched
        ? form.ratio
        : (detectRatioFromInputs(sourceText, form.extraConstraint) ?? null);
      /**
       * 2026-08-01 —— 这张图要拿来干什么。
       *
       * ★发的是**卡面那句人话**而不是 sceneKey:同一句话既给用户看、也进 prompt
       *   (与 `ratioComposition` 同一条纪律),界面说的和模型收到的就没有漂的余地。
       * ★`sceneKey` 本来就随提交上送 backend(列表筛选用),但归整请求里从来没有它 ——
       *   归整层知道行业、品牌、产品、配色、字体、画幅,唯独不知道这张图拿来干什么,
       *   只能从用户那句话里猜。
       * ★没从卡片进来 / 卡已下架 → null → 整个字段不发。
       */
      const purpose = sceneUsageOf(form.sceneKey);
      const resp = await posterService.normalizeInput({
        userText: sourceText,
        mode: "full",
        brandAssets: buildBrandAssetsMeta(),
        // 2026-07-29(prompt-assembly W1)— context 带上**已经选定的全部素材**。
        // 此前只发一个 userStyle:归整层看不见产品,只好为「我想出宣传海报」这六个字编
        // 主标题;而产品与配色只能由提交时的 buildRawPrompt 追加到优化产物**外面**。
        // 素材进来之后,归整层才谈得上判断主次,渲染层也才拼得出唯一一份指令。
        context: {
          ...(purpose ? { purpose } : {}),
          ...(styleForReq ? { userStyle: styleForReq } : {}),
          ...(form.product
            ? {
                product: {
                  code: form.product.code,
                  name: form.product.name,
                  ...(form.product.category ? { category: form.product.category } : {}),
                  sellingPoints: form.product.sellingPoints,
                  specs: form.product.specs,
                  ...(form.product.summary ? { summary: form.product.summary } : {}),
                },
              }
            : {}),
          // 2026-07-30 — 跟随态也发 L2。
          // ★上一版这里是 `styleTokens && !followStyle`:跟随态**不发**,理由是已核准色板
          //   全是浅底,深色画风(数据大屏「石墨黑深色基底」等)借到 bg #F8FAFF 会与画风
          //   tone 当场对撞。那是**拿丢信息去躲一个色板缺陷** —— 而且只躲住了这一条路:
          //   RAW 路径(不点 AI 优化)照旧拼那份错配的色板,同一个选择两条路发不同的指令。
          //   现在深色板补齐了(styleTokenPresets `dark_tech` / `dark_cinema`,
          //   `toneToTokens.test.ts` 有闸钉着声明与 bg 亮度一致),矛盾从根上没有了,
          //   两条路口径也就能归一 —— 判据只剩一个:选了配色就发。
          ...(form.styleTokens
            ? {
                styleTokens: {
                  palette: form.styleTokens.palette,
                  material: form.styleTokens.material,
                  lighting: form.styleTokens.lighting,
                  tone: form.styleTokens.tone,
                  density: form.styleTokens.density,
                },
              }
            : {}),
          // ★判据是「拿得出短语」而不是「key 不是 auto」:陈年草稿回填一个已经下架的
          //   字体 key 时 `fontPhraseForKey` 返 null,老写法会发 `fontStyle: null` ——
          //   与同一处「没选的东西不发字段」的口径相反(发 null 让 ai 侧 default 失效)。
          ...(fontPhrase ? { fontStyle: fontPhrase } : {}),
          // 2026-07-30 —— 画幅这一条改了两处口径。
          //
          // ★病根一:`form.ratio` 默认就是 "1:1" 且**永不为空**,所以这个 `form.ratio ?`
          //   的守卫从来没挡住过任何东西 —— 「智能画幅」态下归整层被告知 1:1,而提交时
          //   裁决出来的可能是 3:4。模型按方版构图、图按竖版出,构图与画幅当场打架,
          //   而这种错在成品上看不出"是个错"(构图也能看,只是不该是那样)。
          //   判据改成「这一档是不是真定下来了」:用户手选过,或文案里写了渠道/方向词。
          //   都没有 → 整个字段不发,让 ai 侧关键词快路自己判(小阳那条路也吃这个)。
          // ★病根二:只发三个字("3:4")等于没告诉它这一档怎么构图。带上构图法则 ——
          //   与出图指令里的【画幅构图】段**同一个真源**(`ratioComposition`),
          //   归整层与出图引擎因此不可能收到两套版面规矩。
          //   (2026-07-31:那句法则已去掉版面角色名词,只剩几何 —— 归整层的产物
          //    正是要发给出图引擎的,所以这条也必须干净。)
          // ★2026-09-09 拍板:copyRoom 留白句全拆(E-01 回滚) —— 构图短语退回只看画幅,
          //   0907 那版「带上文案量做条件性留白」的第二参随之作废(0910 清掉残余传参)。
          ...(ratioForNormalize
            ? {
                ratio: ratioCompositionPhrase(ratioForNormalize) ?? ratioForNormalize,
              }
            : {}),
          ...(form.extraConstraint.trim() ? { extraConstraint: form.extraConstraint.trim() } : {}),
          // 2026-07-30 — 联系方式也得让归整层看见,理由与产品那条一样:它要**排版**这张海报,
          // 而「有一行电话要放」会改变它对角落/底部空间的安排。只在本次要印时发 ——
          // 与提交侧 `brand.phone/email` 同一个闸,两处分叉就会出现「归整层给电话留了位、
          // 出图指令里却没有电话」这种空着一块的版面。
          ...(form.printContact && (brand?.phone || brand?.email)
            ? {
                contact: {
                  ...(brand?.phone ? { phone: brand.phone } : {}),
                  ...(brand?.email ? { email: brand.email } : {}),
                },
              }
            : {}),
        },
        // V77 (2026-06-17) — 用户已手选比例就别让后端再放一炮 deepseek 推荐比例(反正下面也不回填)。
        needSuggestedRatio: !form.ratioUserTouched,
      });
      // Wave 3 — prompt 保留为原文(snapshot),contentDescription 单独承担"AI 改后可改"角色。
      if (seq !== normalizeSeqRef.current) return; // 已有更新一发在途/已落,本返程作废
      if (preserveSnapshot !== null) {
        form.setOriginalPromptSnapshot(preserveSnapshot);
      }
      // 手改守卫(见 lastNormalizedRef 注释):用户改过框里的字 → 只跳过 contentDescription
      // 覆盖(他的字比重跑的归整稿值钱),约束/吸收快照照常更新。
      // ★回写前收进文案框上限(老板实测:润色后 文案超 2000)。裁的是 AI 改写产物,
      //   用户原话/风格走 buildRawPrompt 追加段不受影响。lastNormalizedRef 存裁过的值 ——
      //   否则下一轮手改守卫拿原始超长稿比对,会把「框里已被裁短」误判成用户手改。
      const rendered = capPolishResult(resp.rendered?.contentDescription ?? "");
      const userEdited =
        !opts.explicit &&
        lastNormalizedRef.current !== null &&
        form.contentDescription !== lastNormalizedRef.current;
      if (!userEdited && rendered) {
        form.setContentDescription(rendered);
        lastNormalizedRef.current = rendered;
      }
      form.setSystemConstraints(resp.rendered?.systemConstraints ?? "");
      form.setCoverageChips(resp.coverageChips ?? []);
      form.setIsAiOptimized(true);
      // ★记下这一份产物**吸收了什么** —— 提交时据此决定还要不要追加【产品】【风格】
      //   【字体风格】(见 buildRawPrompt)。用 styleForReq 而不是 form.selectedStyle:
      //   chip 变更那条路 state 还没落,读 closure 拿到的是旧值(同 userStyleOverride 那笔账)。
      form.setAbsorbed(
        absorbedMaterials({
          product: form.product,
          selectedStyle: styleForReq,
          // ★指纹与上送口径必须逐字同源:上面发什么,这里就记什么。
          //   两边一旦分叉,「已吸收」这个判断就会撒谎 —— 记了没发出去的东西 = 提交侧
          //   据此不追加 = 用户选的配色静默消失;反过来则是同一段风格发两遍。
          styleTokens: form.styleTokens,
          fontStyle: form.fontStyle,
        })
      );
      // 2026-05-25 — 比例优先级 ③ AI 推荐:仅当用户没在弹窗手选过比例时回填,WYSIWYG。
      // 用户手选(ratioUserTouched)优先级高于 AI 推荐,不覆盖;文案显式比例在提交时再裁决(最高优先级)。
      if (resp.suggestedRatio && !form.ratioUserTouched) {
        form.setRatio(resp.suggestedRatio);
      }
    } catch (e) {
      const msg = errorText(e, { fallbackTitle: "AI 优化失败" });
      form.setOptimizeFailureMessage(`AI 优化暂不可用,可直接用您写的文案提交。(${msg})`);
      // ★失败时清掉快照:产物没更新,提交必须退回「追加」那一侧,否则用户刚选的产品
      //   会被当成「已经在产物里」而整个消失 —— 这一批要消灭的正是这种静默丢失。
      form.setAbsorbed(null);
    } finally {
      form.setPolishing(false);
    }
  }

  async function handleAiOptimize() {
    if (!form.prompt.trim()) return;
    // 首次优化:记录原文 snapshot
    await runNormalize(form.prompt, form.prompt, undefined, { explicit: true });
  }

  function handleRestoreOriginal() {
    if (form.originalPromptSnapshot === null) return;
    form.setPrompt(form.originalPromptSnapshot);
    form.setOriginalPromptSnapshot(null);
    form.setContentDescription("");
    form.setSystemConstraints("");
    form.setCoverageChips([]);
    form.setIsAiOptimized(false);
    form.setOptimizeFailureMessage(null);
    form.setAbsorbed(null);
  }

  async function handleReOptimize() {
    // 用原文重跑(不是当前 textarea 改过的 contentDescription)
    const source = form.originalPromptSnapshot ?? form.prompt;
    if (!source.trim()) return;
    // 重新优化保留原 snapshot(不覆盖)
    await runNormalize(source, null, undefined, { explicit: true });
  }

  /**
   * 2026-05-18 — 风格 chip 变更入口。
   *
   * 2026-07-29(prompt-assembly W1)— 这里**不再**自己触发 re-run:重优化统一由下方
   * 那个 effect 按「素材指纹变了没有」触发。原因是需要 re-run 的素材从 1 项(风格)
   * 变成了 4 项(产品 / 画风 / 配色 / 字体档),每个入口各写一遍 re-run 必然漏 ——
   * 而漏掉的那一项会以「产物里是旧的、提交时又追加一份新的」的形式发作,
   * 正是这一批在修的那个形状。
   *
   * 未优化时:只更新 state,handleGenerate 在 RAW 路径会尾追加【风格】rich_text。
   */
  function handleStyleChange(newStyle: string | null) {
    form.setSelectedStyle(newStyle);
  }

  /**
   * ★素材变了 → 重新优化(已优化状态下的唯一 re-run 触发点)。
   *
   * 「吸收快照」已经保证了**不丢**:没 re-run 的素材在提交时会被 buildRawPrompt 追加回来。
   * 但追加回来的那一份缺了归整层的判断 —— 产品卖点不会被挑进「要画的字」,配色会变成
   * 第二段【风格】。所以指纹一变就重跑一次,让产物始终是那份唯一的终稿。
   *
   * ★依赖用**指纹字符串**而不是对象:`toneToTokens` 每次返回新对象,按引用比会在
   * 「切到同一个画风」时白跑一次 LLM。
   * ★`polishing` 不进依赖数组 —— 它在 runNormalize 里被置真/置假,进来会让 effect
   * 在优化结束那一刻立刻自激重跑。用 ref 读它的当下值即可。
   */
  const fingerprint = absorbedMaterials({
    product: form.product,
    selectedStyle: form.selectedStyle,
    styleTokens: form.followStyle ? null : form.styleTokens,
    fontStyle: form.fontStyle,
  });
  const polishingRef = useRef(form.polishing);
  polishingRef.current = form.polishing;
  /** 0811:在途时换产品/画风,这一次指纹变更会被下面的 early-return 吞掉,而依赖
   *  此后不再变 —— 换的产品**永远**不会被吸收(素材静默蒸发)。记一笔,polishing
   *  结束后由下面的补偿 effect 一次性补跑;不把 polishing 放进本 effect 依赖,
   *  避免「absorbed 永不收敛 → 无限重跑」的环(absorb 测试实测会 5s 超时)。 */
  const missedWhilePolishingRef = useRef(false);
  useEffect(() => {
    if (!form.isAiOptimized) return;
    if (polishingRef.current) {
      missedWhilePolishingRef.current = true;
      return;
    }
    if (
      form.absorbed &&
      form.absorbed.productCode === fingerprint.productCode &&
      form.absorbed.styleSignature === fingerprint.styleSignature
    ) {
      return;
    }
    const source = form.originalPromptSnapshot ?? form.prompt;
    if (!source.trim()) return;
    // 不 await —— 让 chip / 产品选择的视觉立即响应,优化在后台跑
    // (polishing 会禁掉 SubmitFooter,防止半成品提交)。
    void runNormalize(source, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.isAiOptimized, fingerprint.productCode, fingerprint.styleSignature]);
  useEffect(() => {
    if (form.polishing || !missedWhilePolishingRef.current) return;
    missedWhilePolishingRef.current = false; // 一次性补偿,绝不成环
    if (!form.isAiOptimized) return;
    if (
      form.absorbed &&
      form.absorbed.productCode === fingerprint.productCode &&
      form.absorbed.styleSignature === fingerprint.styleSignature
    ) {
      return;
    }
    const source = form.originalPromptSnapshot ?? form.prompt;
    if (!source.trim()) return;
    void runNormalize(source, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.polishing]);

  async function handleGenerate(
    resolveMask?: () => Promise<File | null>,
    groupStage?: "cover" | "all",
    quoteId?: string | null,
    /**
     * 确认卡上用户过目/改过的画幅(2026-09-09)。
     *
     * ★为什么要显式传而不是读 state:确认卡 onConfirm 里 `setRatio + setRatioUserTouched`
     *   与 `doGenerate` 在同一拍执行,React state 还没落,closure 读到的仍是「没确认」——
     *   于是下面 smartActive 判真、**再抽一次** 5 选 1,卡上说 9:16 提交发 3:4
     *   (测试机月饼单实锤;与 runNormalize 的 userStyleOverride 同族病、0831 爱民
     *   「弹卡问完照样再判一次」的智能路残留)。传了它 = 卡上那一档就是终值,不再判。
     */
    confirmedRatio?: Ratio | null
  ) {
    if (!brand) return;
    // 2026-05-25 — i2i 模式必须先上传参考图。
    if (form.mode === "I2I" && !form.refImage) {
      toast({ title: "请先上传参考图", variant: "destructive" });
      return;
    }
    // W2-B9 多图参考:组图 / 套图态不带(卡片上已说明),其余态要过两道闸 ——
    // 模型得支持(ai 能力表),且不能与圈选同用(api / ai 都会拒,早说比失败好)。
    const refImageRefs = refImagesBlockedReason({
      groupMode: form.groupPoints !== null,
      ratioSetMode: !!form.ratioSet && form.ratioSet.length >= 2,
    })
      ? undefined
      : toRefImageRefs(form.refImages);
    if (refImageRefs) {
      if (!multiRefSupported) {
        toast({
          title: "当前出图档位不支持多图参考",
          description: "请在 ⚙ 参数里换成标准 / 高清 / 旗舰 / 精绘档,或移除参考图。",
          variant: "destructive",
        });
        return;
      }
      if (form.mode === "I2I" && form.maskPainted) {
        toast({
          title: "多图参考不能与圈选同用",
          description: "请去掉圈选(按整图改),或移除参考图。",
          variant: "destructive",
        });
        return;
      }
    }
    form.setSubmitting(true);
    try {
      // ★圈选:提交这一刻才导出 + 上传(整段都在 submitting 忙碌态里,没有边传边点的窗口)。
      //   失败**中止提交**并说清楚 —— 静默按整图重绘 = 用户圈了等于没圈。
      let maskRef: string | undefined;
      if (form.mode === "I2I" && resolveMask) {
        try {
          const file = await resolveMask();
          if (file) maskRef = (await posterService.uploadMaskImage(file)).objectKey;
        } catch {
          toast({
            title: "涂选区域没能保存",
            description: "这次没有出图;重试一次,或点「重涂」后按整张图重绘。",
            variant: "destructive",
          });
          form.setSubmitting(false); // ★早退必须复位,否则按钮永久禁用(本函数其余早退处同款)
          return;
        }
      }
      // V7 (2026-05-12) — logoId/qrId 两个 state 都可空,语义 "本次不使用"。
      // 找不到对应 brand 项时直接给 undefined,后端 buildRecomposeBrand 跳过该 overlay。
      const logo = form.logoId ? brand.logos.find((l) => l.id === form.logoId) : undefined;
      const qr = form.qrId ? brand.qrs.find((q) => q.id === form.qrId) : undefined;
      // V26 Wave 5 — 提交时用 valid* 派生值(永远合法),不用 raw state。
      // 派生已经处理:thinking 收 count/ratio/tier,combinationDeny 自动跳合法 default。
      // Phase D (2026-05-16) — 不再发 sizePreset,直接发 ratio + resolutionTier。
      // backend ai-service 按 (provider, ratio, tier) 三元组查真实 W×H。
      // validTier 若为 null,fallback "2K"(满足 PosterTaskCreateRequest 必填 resolutionTier 约束)。
      // 0831 — 比例裁决收口进 `resolveRatioRuling`(detectRatio.ts,提交/画幅框/底栏/弹卡四处同源):
      //   pinned(手选 / i2i 跟原图 / 确认卡确认值) > 显式数字比例 > 语境化词面 > LLM 推断 > 默认。
      // 取代 2026-05-25 版「文案一律最高」—— 真机实锤(爱民 任务 5492005929984):备注里的
      // 「正方形模块」是内容元素不是画幅意图,词面却把手选 3:4 盖成 1:1;更狠的是确认卡
      // 问完、setRatio+touched 落定后,这里照样拿文案再盖一遍(弹卡处「不会再判一次」的
      // 注释是假的)。现在:显式比例 vs 手选的冲突在 requestGenerate 弹卡问,答案回填成
      // pinned 到这里天然生效;本处只认裁决结果,不再单独抬高文案。
      // 2026-07-17 — 备注(特别要求)也一起扫:生产实锤用户把「9:16尺寸」写进备注被漏 → 退 1:1。
      const promptText = form.originalPromptSnapshot ?? form.prompt;
      const userText = [promptText, form.extraConstraint].filter((s) => s && s.trim()).join("\n");
      const textDetection = detectRatioDetailedFromInputs(promptText, form.extraConstraint);
      // V76 (2026-05-28) — 「智能尺寸」真智能:用户没手选 ratio + 文案没显式比例词 + 不是 i2i 跟原图
      // 时,提交前调一次 backend /poster/infer-ratio 让 LLM 5 选 1(ai-service 关键词 miss 时调
      // LLM,失败兜底 4:5)。比走 normalize 全量 Qwen 归整轻得多,平均 < 1s。
      // 修「智能尺寸大多方图」bug — 旧代码 textRatio=null + 用户没手选 → validRatio=1:1 默认。
      const isI2I = form.mode === "I2I";
      // ★2026-07-31:组图/套图态**不推断** —— ⚙ 里根本没给「智能」这一档,右回显列也
      //   写着固定比例,而这里照样在推,用户按 1:1 做的决定实际可能发 4:5(e2e 抓到)。
      const smartActive =
        !confirmedRatio &&
        shouldInferRatio({
          mode: detectOutputMode(form.groupPoints, form.ratioSet),
          isI2I,
          textRatioFound: !!textDetection,
          ratioUserTouched: form.ratioUserTouched,
          hasText: userText.trim().length > 0,
        });
      let inferredRatio: Ratio | null = null;
      if (smartActive) {
        try {
          const inferResp = await posterService.inferRatio(userText);
          inferredRatio = inferResp.ratio as Ratio;
        } catch {
          // 失败兜底 — 让 validRatio (默认 1:1) 接管;不阻断 submit
        }
      }
      // i2i 没手选=跟随原图,validRatio 是选图时量出来的真值 —— 与手选同级 pin 住,
      // 词面不许把它盖掉(此前会:显示侧从不看词面、提交侧看,同一单两个口径)。
      const ratioRuling = resolveRatioRuling({
        detection: textDetection,
        // 确认卡的终值与手选同级 pin(用户亲眼过目过) —— state 这一拍还没落,只能显式用参数
        pinnedRatio: confirmedRatio ?? (form.ratioUserTouched || isI2I ? validRatio : null),
      });
      const ratioForReq: Ratio = ratioRuling.ratio ?? inferredRatio ?? validRatio;
      // W2-B9:带参考图一律 1K(保真参数只在 1K 送达;api 会拒别的档)。
      const tierForReq: ResolutionTier = refImageRefs ? "1K" : (validTier ?? "2K");

      const filterTouched = Object.values(form.filter).some(
        (v) => typeof v === "number" && v !== 0
      );

      // Wave 3 Task 15 — 按 isAiOptimized 拼装 rawPrompt:
      // - 已 AI 优化 → "[AI-OPTIMIZED]\n{contentDescription}\n\n{systemConstraints}"
      //   前缀让 backend Task 7 shouldRunMinimal 检测到 → 跳 minimal 兜底
      // - 未优化 → form.prompt(走 backend minimal 兜底补 headline/style/scene)
      //
      // 2026-05-18 — 风格独立注入:user 选了 chip 就尾追加【风格】rich,
      // 跟优化与否解耦。AI 优化路径走 context.userStyle → Qwen visual_style.tone,
      // 不再这里 double 加,只对 RAW 提交注入。
      //
      // V0 (2026-07-25):拼装挪进纯函数 `buildRawPrompt` —— 它现在还要拼 L2 配色
      // (【风格】段是 L2 到达引擎的**唯一**通道,事实③),这种东西得能被直接测,
      // 不该埋在 brand/ratio/quota 一堆副作用中间。逐条行为见 buildRawPrompt.test.ts。
      const rawPromptForSubmit = buildRawPrompt({
        isAiOptimized: form.isAiOptimized,
        prompt: form.prompt,
        contentDescription: form.contentDescription,
        systemConstraints: form.systemConstraints,
        selectedStyle: form.selectedStyle,
        // ★AI 优化路径下跟随态的 L2 不参与(它是 L1 的副本,L1 已进归整层);
        //   RAW 路径不受影响 —— 那条路没有归整层,L2 仍是结构化配色的唯一来源。
        styleTokens: form.isAiOptimized && form.followStyle ? null : form.styleTokens,
        fontStyle: form.fontStyle,
        // 波B:产品库直选的资料逐字进指令(没选就是 null,存量行为一个字不变)
        product: form.product,
        showProductSpecs: form.showProductSpecs,
        // ★归整层吸收过的素材不再在这里追加(否则就是两段打架的【风格】);
        //   吸收不到的照旧追加 —— 宁可重复也不丢。逐条行为见 buildRawPrompt.test.ts。
        absorbed: form.absorbed,
        // ★传**裁决完**的那个比例(文案显式 > 手选 > AI 推荐 > 默认),不是 form.ratio:
        //   图按 ratioForReq 出,【画幅构图】段必须写同一档的法则,否则指令自相矛盾。
        // ★i2i 不注入:那条路的 rawPrompt 是**编辑指令**(ai `_build_variants_freeform_raw`
        //   见 cover_image_ref 判据,走 `_I2I_EDIT_INSTRUCTION_TEMPLATE` 不套生成 wrapper)。
        //   往「把背景换成纯白」后面缀一句「主体居中占画面七成」= 让模型重新构图,
        //   正是那条注释警告的「修改说明被埋进整段海报设计描述,改不动原图」。
        ratio: isI2I ? null : ratioForReq,
      });
      //
      // ★上限拦在提交前(`RAW_PROMPT_MAX`,ai 侧 `max_length=2000`)。
      // 文案框的 2000 只管用户打的字,【风格】【配色】【字体风格】是**提交时追加**的,
      // 一个字都不算在那个计数器里 —— 近上限的文案 + 选了画风,过去是直接挂在后端,
      // 用户只看到一句"生成失败",完全不知道是哪儿多了。这里说清楚超了多少、能怎么办。
      const over = rawPromptOverflow(rawPromptForSubmit);
      if (over > 0) {
        toast({
          title: `文案连同风格设置超出 ${over} 字`,
          description: `出图指令上限 ${RAW_PROMPT_MAX} 字,画风/配色/字体的说明也算在内。精简文案,或去掉其中一项风格设置。`,
          variant: "destructive",
        });
        form.setSubmitting(false);
        return;
      }

      // ── B1 (2026-08-03):提交侧口径 = 有字卡(空卡滤掉,卡面早有「生成时跳过」提示)。
      // 前身 W4 在这里收编草稿行 —— 草稿行已随 B1 退役(添加=直接追加空卡),
      // 「打了字没点添加」这个要收编的状态不存在了。UI 里的空卡**留在原地**不动:
      // 用户回来还能接着填,只是这一次不出它。
      const collectedPoints = submittableGroupPoints(form.groupPoints);

      // ★红线闸(2026-08-14):蓝图带来的参考句现在是**值**(可见、可改),但用户可能一个字
      // 没动就点生成 —— 那就是把别人家的宣传语印进自家物料(0813 真出过)。
      // 只拦「逐字未动」的那几张,改过一个字就放行;用户确认了就照出(他的物料他做主)。
      const untouched = untouchedReferenceTitles(form.groupPoints);
      if (untouched.length > 0 && confirmUntouchedReference) {
        const ok = await confirmUntouchedReference(untouched);
        if (!ok) {
          form.setSubmitting(false);
          return;
        }
      }

      // P-B (2026-07-20):组图 / 多比例套图 —— 二者互斥(组图优先;UI 已互斥,此处兜底)。
      const activeGroupPoints =
        collectedPoints && collectedPoints.length > 0 ? collectedPoints : null;
      const activeRatioSet =
        !activeGroupPoints && form.ratioSet && form.ratioSet.length >= 2 ? form.ratioSet : null;
      const req: PosterTaskCreateRequest = {
        ...(form.sceneKey ? { sceneKey: form.sceneKey } : {}), // B6 场景打标
        // 没写文案时用产品名兜底 —— 比「未命名」有用,且仍是库里的原文(不编)
        // ★1004 T8:产品名优先(场景卡的 prompt 是指令模板,取它前 30 字作品全叫「保留画面里的…」)
        name: posterTaskName({
          productName: form.product?.name,
          sceneTitle: sceneLabelOf(form.sceneKey),
          prompt: form.prompt,
          fallback: isI2I ? "图改图" : "未命名",
          at: new Date(),
        }),
        renderMode: "SERVER_OVERLAY",
        provider: form.provider,
        ratio: ratioForReq,
        resolutionTier: tierForReq,
        requestedCount: validCount,
        // 组图:每爆点一张(title≤30/detail≤120 已在编辑器钳);蓝图溯源随包
        groupPoints: activeGroupPoints ?? undefined,
        // B2-1 分步出图:只在真组图(>1 张)时有意义 —— 1 张的"先出封面"就是全出。
        groupStage: activeGroupPoints && activeGroupPoints.length > 1 ? groupStage : undefined,
        blueprintId: form.explosive?.blueprintId,
        formulaName: form.explosive?.formulaName,
        formulaPayload: activeGroupPoints ? form.explosive?.payload : undefined,
        // 多比例套图:同内容一次出多比例
        ratios: activeRatioSet ?? undefined,
        // W6 主体一致性:只在真多张(组图/套图)时发 —— 单张没有「一致」可言,
        // 后端也会忽略,但不发更省得让人以为单张也在串行。
        keepSubjectConsistent:
          (activeGroupPoints && activeGroupPoints.length > 1) ||
          (activeRatioSet && activeRatioSet.length > 1)
            ? form.keepSubjectConsistent
            : undefined,
        // 组图产品锚(2026-08-03 A2):adopt 后的 uploads objectKey,backend 兑换 URL 改键
        // anchorImageUrl 走 B2-1 外部锚通路。只在真组图时发(后端无台本也会剥,双保险)。
        groupAnchorRef: activeGroupPoints && form.groupAnchorRef ? form.groupAnchorRef : undefined,
        // B①主体锚句:排台本产出,ai 逐张注入(有实拍锚时也无害)
        groupSubjectSpec:
          activeGroupPoints && form.groupSubjectSpec ? form.groupSubjectSpec : undefined,
        // 2026-05-25 — i2i:用户上传参考图作 base,后端签 URL 喂 orchestrator image_edit。
        mode: isI2I ? "I2I" : undefined,
        coverImageRef: isI2I ? form.refImage?.objectKey : undefined,
        // R5:涂了 mask 才带 —— 不带 = 整图重绘(ai 侧据此分派 image_inpaint / image_edit)
        maskImageRef: maskRef,
        // W2-B9 多图参考(≤4 张 + 用途);不带 = 不发这个键。报价按 1K 单价,参考图不另计费。
        refImageRefs,
        // V11 (2026-05-12) — FREEFORM_RAW 真自由创作: rawPrompt 直传, ai-service
        // 短路不再拼"产品: X 卖点: Y" 模板化骨架。productName 仅占位(后端 @NotBlank 兜底)。
        content: {
          sourceType: "FREEFORM_RAW",
          productName:
            sliceSafe(form.prompt.trim(), 30) ||
            sliceSafe(form.product?.name ?? "", 30) ||
            "自由创作",
          sellingPoints: [],
          description: "",
          rawPrompt: rawPromptForSubmit,
          // 2026-09-01(爱民 hint 叠加):用户原文另存一份。rawPrompt 是拼装后的成串,
          // 「用此提示词重新创作」回填它会把【画幅构图】等追加段再叠一层;回填只认这份。
          userPrompt: form.prompt.trim() || undefined,
        },
        brand: {
          logoUploadId: logo?.objectKey,
          qrCodeMode: qr ? qr.mode : "NONE",
          qrCodeUploadId: qr?.objectKey,
          qrCodeUrl: qr?.url,
          // V8.4 reverted (2026-05-12) — 真自由创作:不送 posterTitle/posterSubtitle,
          // AI 从 prompt 自己抽标题,模板 fallback 空字段(单产品用 productName)。
          //
          // 2026-07-30 — 加 `printContact` 闸。
          // ★V11.6 那条注释写着「ai-service prompt_builder 接通已久,用户填一次就生效」,
          //   **那句话对 composer 路径成立、对自由创作从来不成立**:自由创作走 FREEFORM_RAW
          //   短路(`orchestrator._build_variants_freeform_raw`),它不读 brand.phone;
          //   而 `compositor.prepare_brand_assets` 算出来的 `contact_lines` 自 v3(2026-05-07)
          //   起也没有任何消费方(画文字的责任那次整体交给了 prompt)。
          //   于是「填了电话 → 海报上永远没有电话,且不报错」这条死通道存活了两个多月。
          //   现在两头都接上了(ai 侧短路补拼 + 归整层 context.contact),这里只剩一个问题:
          //   要不要印。默认关 —— 见 `useFreeCreateForm.printContact` 的理由。
          phone: (form.printContact && brand?.phone) || undefined,
          email: (form.printContact && brand?.email) || undefined,
          // V11.6 — 本次启用品牌色才发, off 时不发 (ai-service 走 logo 采样 hex 兜底)
          brandColors:
            form.useBrandColors && brand?.colors
              ? {
                  primary: brand.colors.primary,
                  secondary: brand.colors.secondary,
                  accent: brand.colors.accent,
                }
              : undefined,
        },
        creative: undefined,
        layout: undefined,
        template: undefined,
        initialFilter: filterTouched ? form.filter : undefined,
        // 任务 14:用户在品牌卡里选的九宫格锚点(都选「自动」时仍是 undefined)。
        // ★此前这里恒 undefined —— 契约全通、旁路(小阳)能设,唯独手动主线不能。
        layoutOverrides: buildLayoutOverrides({
          logoAnchor: form.logoAnchor,
          qrAnchor: form.qrAnchor,
          hasLogo: !!form.logoId,
          hasQr: !!form.qrId,
        }),
        // #1 (2026-07-04) — 自由创作「特别要求」透传(此前恒 undefined,自由侧不接 extraConstraint)。
        // 2026-07-13 — i2i 用户没手选比例 = 跟随原图:form.ratio 已在选图时量图回填,再带
        // ratioFollowsSource 让 ai 端按参考图实测兜底(前端量图失败时不至于退回 1:1)。
        creativeOverrides: buildCreativeOverrides(
          form.extraConstraint,
          isI2I && !form.ratioUserTouched
        ),
        // 备注落库:「特别要求」原文存 task.notes,供详情页展示 + 重新创作回填(与进图的 extraConstraint 同源)。
        notes: form.extraConstraint.trim() || undefined,
        // 2026-05-26 — 把用户选的风格结构化回传(短 label,非 rich tone),详情页「本次设置」展示。
        // ★短 label 是硬要求:selectedStyle 是 100+ 字 rich tone,直发会撞 backend @Size(80)。
        // V0 (2026-07-25) — 拼装挪进 `buildStyleName`:多了「用户自己选过的配色」这一项,
        // 长度纪律与省略规则一起在那里单测(见 buildStyleName.test.ts)。
        styleName: buildStyleName({
          selectedStyle: form.selectedStyle,
          fontStyle: form.fontStyle,
          styleTokens: form.styleTokens,
          followStyle: form.followStyle,
        }),
        // v1.4 (2026-05-14) — FreeCreate 不发 useIndustryVisualHint,ai-service 端
        // FREEFORM_RAW 路径强制跳所有 hint/wrapper。
        useGptThinking: form.provider === "OPENAI" ? form.useGptThinking : undefined,
      };
      // PST-CF-046 — 定时投放走排期端点(对齐 apply-template),不立即生成。
      if (form.scheduleMode === "later" && form.scheduledAt) {
        const sched = await posterService.createSchedule({
          scheduledAt: form.scheduledAt,
          task: req,
        });
        toast({
          title: "已排期",
          description: `任务 #${sched.scheduleId} 将在 ${form.scheduledAt.replace("T", " ")} 触发`,
        });
        form.setSubmitting(false);
        return;
      }
      // ★修复波 0926(代理 D):报价单**必须随建单请求走**。此前这个 quoteId 参数收进来就
      //   没再用过,而没弹确认卡的路(直接点生成)连单都没取 —— 整条出图主路落在后端
      //   `poster_task_without_quote` 软着陆,settle 缺 tier 时按 SKU 级旧口径扣
      //   (0903「报价 28 实扣 40」,api CreateQuoteBinding0926IT 有真库负对照)。
      //   确认卡那张单 = 用户亲眼看过的数,优先用它;没有就在这一刻按**提交时裁决完**的
      //   档位/张数现取一张。取单失败返 null → 照旧提交(后端 WARN 软着陆),不把人堵在门外。
      //   ★定时投放不走这里:单 15 分钟过期,排期触发时绑单必失败,那条路由服务端现开单。
      const boundQuoteId =
        quoteId ??
        (await acquireQuoteId(
          submitQuoteParams({
            provider: form.provider,
            tier: tierForReq,
            groupPoints: activeGroupPoints,
            groupStage: req.groupStage,
            ratioSet: activeRatioSet,
            count: validCount,
          })
        ));
      const res = await posterService.createTask(
        boundQuoteId ? { ...req, quoteId: boundQuoteId } : req
      );
      // UX-2 (2026-05-30) — 提交成功即时反馈,跳详情页前先确认(POST 有 1-3s,避免像没反应被重复点)。
      toast({ title: "已提交生成", description: "正在生成,马上为你呈现…" });
      // 2026-05-25 — 带 from=freecreate,详情页「返回」据此回自由创作页(连续创作),而非首页。
      navigate(`/ai-haibao/tasks/${res.taskId}`, { state: { from: "freecreate" } });
    } catch (e) {
      const t = posterErrorToast(e);
      toast(t);
      form.setSubmitting(false);
    }
  }

  return {
    saveDraftPending: saveDraftMut.isPending,
    saveDraft: () => saveDraftMut.mutate(),
    handleAiOptimize,
    handleRestoreOriginal,
    handleReOptimize,
    handleStyleChange,
    handleGenerate,
  };
}
